import React, { ReactElement } from 'react';
import { SupportedSize } from '../Element/Element';
import GutterBag from '../GutterBag/GutterBag';

type StepsProps = {
  /** Index of the active step */
  selectedStepIndex: number;
  /** Function with arguments (index, selectedStepIndex) that determines whether a step is active */
  isStepActive?: (index: number, selectedStepIndex: number) => boolean;
  /** Function with arguments (index, selectedStepIndex) that determines whether a step is completed */
  isStepCompleted?: (index: number, selectedStepIndex: number) => boolean;
  /** Function with arguments (index, selectedStepIndex) that determines whether a step is selectable */
  isStepSelectable?: (index: number, selectedStepIndex: number) => boolean;
  /** Event handler with arguments (event, index), where index is the index of the selected step */
  onSelect?: (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    index: number
  ) => void;
  /** Boolean that enables centering */
  centerSteps?: boolean;
  /** Boolean that enables filling the component width */
  spaceBetweenSteps?: boolean;
  /** An element (e.g. Icon) that appears inside a "completed" state indicator - true will use the default icon */
  completionIcon?: React.ReactNode | boolean;
  /** Boolean that triggers an always-bright variant for completed state items */
  alwaysBrightCompleted?: boolean;
  /** A number of Step elements */
  children: ReactElement[];
  /** A size of a padding between steps */
  paddingSize?: SupportedSize;
};

const Steps: React.FunctionComponent<StepsProps> = ({
  selectedStepIndex,
  isStepActive = (index, selectedStepIndex) => index === selectedStepIndex,
  isStepCompleted = (index, selectedStepIndex) => index < selectedStepIndex,
  isStepSelectable = (index, selectedStepIndex) => index < selectedStepIndex,
  onSelect = () => {},
  centerSteps,
  spaceBetweenSteps,
  completionIcon,
  alwaysBrightCompleted,
  children,
}) => {
  const stepCount = React.Children.count(children);

  selectedStepIndex = selectedStepIndex
    ? Math.min(stepCount - 1, selectedStepIndex)
    : 0;

  const stepIndicatorElements = React.Children.map(
    children,
    (child: ReactElement, index) => {
      return React.cloneElement(child, {
        active: isStepActive(index, selectedStepIndex),
        completed: isStepCompleted(index, selectedStepIndex),
        selectable: isStepSelectable(index, selectedStepIndex),
        onSelect:
          child.props.onSelect ||
          ((event: React.MouseEvent<HTMLDivElement, MouseEvent>) =>
            onSelect(event, index)),
        completionIcon,
        alwaysBrightCompleted,
      });
    }
  );

  return (
    <GutterBag
      contents={stepIndicatorElements}
      centeredHorizontally={centerSteps}
      spaceBetween={spaceBetweenSteps}
    />
  );
};

export default Steps;
